import '@wix/design-system/styles.global.css';

import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider, initI18n } from '@wix/wix-i18n-config';
import { createEssentials, PanoramaProvider } from '@wix/fe-essentials-standalone';
import { RootEssentialsProvider } from '@wix/fe-essentials-standalone/react';
import { initAppStore } from './context';
import { WixDesignSystemProvider } from '@wix/design-system';
import { config, panoramaGlobalConfig } from './config';
import { ARTIFACT_ID, SENTRY_DSN } from './application-constants';
import { panoramaClientFactory, PanoramaPlatform } from '@wix/panorama-client-web';
import { ciBuildConfig } from './ciBuildConfig';
import { AppRouter } from './router/AppRouter';

const i18n = initI18n({
  locale: window.__LOCALE__,
  messages: window.__MESSAGES__ || {},
});

const essentials = createEssentials({
  environment: {
    language: 'en',
    artifactId: ARTIFACT_ID,
    version: 'my-version',
  },
  experiments: {
    bag: config.experiments,
  },
  fedops: {},
  dataCapsule: {
    namespace: ARTIFACT_ID,
    storageStrategy: 'localStorage',
  },
});

const domNode = document.getElementById('root')!;
const root = createRoot(domNode);
initAppStore();

const panoramaFactory = panoramaClientFactory({
  baseParams: {
    platform: PanoramaPlatform.Standalone,
    fullArtifactId: ARTIFACT_ID,
    artifactVersion: ciBuildConfig.CI_FINGERPRINT,
    componentId: 'gg-business-starter-app',
  },
  pluginParams: {
    sentry: window.Sentry,
    sentryDsn: SENTRY_DSN,
    // to report unhandled Errors
    sentryMain: true,
  },
}).withGlobalConfig(panoramaGlobalConfig);

// init screen & flow
// must be outside react render cause the first event loop should already contain the correct flow
let pathname = window.location.pathname;
if (config.env === `production`) {
  pathname = pathname.replace(config.baseUrl, '');
}

root.render(
  <Suspense fallback="...loading">
    <PanoramaProvider value={panoramaFactory.client()}>
      <I18nextProvider i18n={i18n}>
        <RootEssentialsProvider essentials={essentials}>
          <WixDesignSystemProvider features={{ newColorsBranding: true }}>
            <AppRouter />
          </WixDesignSystemProvider>
        </RootEssentialsProvider>
      </I18nextProvider>
    </PanoramaProvider>
  </Suspense>,
);
